import { openDB } from 'idb';

const DB_NAME = 'ETPOS_OfflineDB';
const DB_VERSION = 2;

export const STORES = {
  PRODUCTS: 'products',
  CONTACTS: 'contacts',
  SYNC_QUEUE: 'sync_queue',
};

// Initialize the IndexedDB
export const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Store for caching products
      if (!db.objectStoreNames.contains(STORES.PRODUCTS)) {
        db.createObjectStore(STORES.PRODUCTS, { keyPath: 'id' });
      }
      
      // Store for caching contacts
      if (!db.objectStoreNames.contains(STORES.CONTACTS)) {
        db.createObjectStore(STORES.CONTACTS, { keyPath: 'id' });
      }
      
      // Store for queuing offline actions (e.g. creating a product)
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
      }
    },
  });
};

// --- Product Store Helpers ---

export const saveProductsToOfflineDB = async (products) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.PRODUCTS, 'readwrite');
    // Clear existing cache and add new ones (or you could upsert individual records)
    await tx.objectStore(STORES.PRODUCTS).clear();
    for (const product of products) {
      await tx.objectStore(STORES.PRODUCTS).put(product);
    }
    await tx.done;
    return true;
  } catch (error) {
    console.error("Failed to save products to offline DB", error);
    return false;
  }
};

export const getProductsFromOfflineDB = async () => {
  try {
    const db = await initDB();
    const products = await db.getAll(STORES.PRODUCTS);
    return products || [];
  } catch (error) {
    console.error("Failed to get products from offline DB", error);
    return [];
  }
};

// --- Sync Queue Helpers ---

export const saveContactsToOfflineDB = async (contacts) => {
  try {
    const db = await initDB();
    const tx = db.transaction(STORES.CONTACTS, 'readwrite');
    await tx.objectStore(STORES.CONTACTS).clear();
    for (const contact of contacts) {
      await tx.objectStore(STORES.CONTACTS).put(contact);
    }
    await tx.done;
    return true;
  } catch (error) {
    console.error("Failed to save contacts to offline DB", error);
    return false;
  }
};

export const getContactsFromOfflineDB = async () => {
  try {
    const db = await initDB();
    const contacts = await db.getAll(STORES.CONTACTS);
    return contacts || [];
  } catch (error) {
    console.error("Failed to get contacts from offline DB", error);
    return [];
  }
};

export const addToSyncQueue = async (actionData) => {
  try {
    const db = await initDB();
    await db.add(STORES.SYNC_QUEUE, {
      ...actionData,
      timestamp: Date.now()
    });
    return true;
  } catch (error) {
    console.error("Failed to add to sync queue", error);
    return false;
  }
};

export const getSyncQueue = async () => {
  try {
    const db = await initDB();
    return await db.getAll(STORES.SYNC_QUEUE);
  } catch (error) {
    console.error("Failed to get sync queue", error);
    return [];
  }
};

export const removeFromSyncQueue = async (id) => {
  try {
    const db = await initDB();
    await db.delete(STORES.SYNC_QUEUE, id);
    return true;
  } catch (error) {
    console.error("Failed to remove from sync queue", error);
    return false;
  }
};
